/**
 * @file WmsDashboardCounters.tsx
 * @description Шесть плиток «Обзора склада» (phase19.24 этап 1, шаг 3;
 * phase19.31 шаг О4 — общая плитка `MetricTile`, макеты (в) и (в2)).
 *
 * Плитка — ссылка целиком в отфильтрованный реестр, с наведением по §11.
 * Состав, подписи, пояснения и адреса — из реестра `DASHBOARD_COUNTERS`,
 * тон — только при ненулевом счётчике (`counterTone`).
 *
 * Раскладка: 2×3 на телефоне, 3×2 на 768–1279, 6 в ряд от 1280.
 */

import type { ReactNode } from "react";

import { DASHBOARD_COUNTERS, counterTone, type DashboardCounterMeta } from "./dashboard-counters";
import { DASHBOARD_TEXT } from "./dashboard-texts";
import { MetricTile, MetricTileSkeleton } from "../../ui/MetricTile";
import type { WmsDashboardCountersDto } from "../../../lib/api-wms-dashboard";

interface Props {
  /** Счётчики сервера; `null` — данных ещё нет (заготовки). */
  counters: WmsDashboardCountersDto | null;
  /** База маршрута WMS: `/shop/:slug/wms`. */
  basePath: string;
  /** Склад, выбранный на сводке; пустая строка — все склады. Едет в ссылки. */
  warehouseId?: string;
  loading: boolean;
}

/** Сетка плиток — общая для заготовок и данных, чтобы не прыгала высота. */
function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">{children}</div>;
}

function CounterTile({ meta, value, basePath, warehouseId }: {
  meta: DashboardCounterMeta;
  value: number;
  basePath: string;
  warehouseId: string;
}) {
  return (
    <MetricTile
      label={meta.label}
      value={value}
      hint={meta.hint(value)}
      icon={meta.icon}
      tone={counterTone(meta, value)}
      to={meta.href({ base: basePath, warehouseId })}
      title={meta.title}
    />
  );
}

export function WmsDashboardCounters({ counters, basePath, warehouseId = "", loading }: Props) {
  if (loading || !counters) {
    return (
      <Grid>
        {DASHBOARD_COUNTERS.map((meta) => (
          <MetricTileSkeleton key={meta.key} />
        ))}
      </Grid>
    );
  }

  return (
    <section aria-label={DASHBOARD_TEXT.countersLabel}>
      <Grid>
        {DASHBOARD_COUNTERS.map((meta) => (
          <CounterTile
            key={meta.key}
            meta={meta}
            value={counters[meta.key] ?? 0}
            basePath={basePath}
            warehouseId={warehouseId}
          />
        ))}
      </Grid>
    </section>
  );
}
